"use strict";
// Copied from AppRTC's sdputils.js:
Object.defineProperty(exports, "__esModule", { value: true });
exports.defaultBandwithOptions = exports.maybePreferCodec = exports.modifyCodecPayloadType = void 0;
// Sets |codec| as the default |type| codec if it's present.
// The format of |codec| is 'NAME/RATE', e.g. 'opus/48000'.
function maybePreferCodec(sdp, type, dir, codec) {
    const str = `${type} ${dir} codec`;
    if (codec === "") {
        return sdp;
    }
    const sdpLines = sdp.split("\r\n");
    // Search for m line.
    const mLineIndex = findLine(sdpLines, "m=", type);
    if (mLineIndex === null) {
        return sdp;
    }
    // If the codec is available, set it as the default in m line.
    const codecIndex = findLine(sdpLines, "a=rtpmap", codec);
    if (codecIndex) {
        const payload = getCodecPayloadType(sdpLines[codecIndex]);
        if (payload) {
            sdpLines[mLineIndex] = setDefaultCodec(sdpLines[mLineIndex], payload);
        }
    }
    sdp = sdpLines.join("\r\n");
    return sdp;
}
exports.maybePreferCodec = maybePreferCodec;
// Find the line in sdpLines that starts with |prefix|, and, if specified,
// contains |substr| (case-insensitive search).
function findLine(sdpLines, prefix, substr) {
    return findLineInRange(sdpLines, 0, -1, prefix, substr);
}
// Find the line in sdpLines[startLine...endLine - 1] that starts with |prefix|
// and, if specified, contains |substr| (case-insensitive search).
function findLineInRange(sdpLines, startLine, endLine, prefix, substr) {
    const realEndLine = endLine !== -1 ? endLine : sdpLines.length;
    for (let i = startLine; i < realEndLine; ++i) {
        if (sdpLines[i].indexOf(prefix) === 0) {
            if (!substr ||
                sdpLines[i].toLowerCase().indexOf(substr.toLowerCase()) !== -1) {
                return i;
            }
        }
    }
    return null;
}
// Gets the codec payload type from an a=rtpmap:X line.
function getCodecPayloadType(sdpLine) {
    const pattern = new RegExp("a=rtpmap:(\\d+) \\w+\\/\\d+");
    const result = sdpLine.match(pattern);
    return result && result.length === 2 ? result[1] : null;
}
// Returns a new m= line with the specified codec as the first one.
function setDefaultCodec(mLine, payload) {
    const elements = mLine.split(" ");
    // Just copy the first three parameters; codec order starts on fourth.
    const newLine = elements.slice(0, 3);
    // Put target payload first and copy in the rest.
    newLine.push(payload);
    for (let i = 3; i < elements.length; i++) {
        if (elements[i] !== payload) {
            newLine.push(elements[i]);
        }
    }
    return newLine.join(" ");
}
// https://www.webrtc-experiment.com/BandwidthHandler.js
// BandwidthHandler.js
function defaultBandwithOptions(sdp, quality, audioBitrate, usedtx = false, stereo = false) {
    let useDTX = 0;
    let useStereo = 0;
    if (usedtx)
        useDTX = 1;
    else
        useDTX = 0;
    if (stereo)
        useStereo = 1;
    else
        useStereo = 0;
    let min;
    let target;
    let max;
    // google recommended numbers for VOD.
    switch (quality) {
        case "4K":
            min = 6000;
            target = 12000;
            max = 17400;
            break;
        case "1080p60":
            min = 1500;
            target = 3000;
            max = 4350;
            break;
        case "1080p":
            min = 900;
            target = 1800;
            max = 2610;
            break;
        case "720p":
            min = 512;
            target = 1024;
            max = 1485;
            break;
        case "480p":
            min = 256;
            target = 512;
            max = 742;
            break;
        default:
            min = 900;
            target = 1800;
            max = 2610;
            break;
    }
    // default is now 1080p numbers
    // recommended settings
    // https://developers.google.com/media/vp9/settings/vod
    const bandwidth = {
        screen: 10 * 1000 * 1000, // 500 kbits minimum
        audio: 512 * 1000, // 512 kbits  maximum
        video: { min, max, target },
    };
    const isScreenSharing = true;
    sdp = BandwidthHandler.setApplicationSpecificBandwidth(sdp, bandwidth, isScreenSharing);
    // sdp = BandwidthHandler.setVideoBitrates(sdp, {
    //   min: bandwidth.video.min,
    //   max: bandwidth.video.max,
    // });
    sdp = BandwidthHandler.setOpusAttributes(sdp, {
        stereo: useStereo, // to disable stereo (to force mono audio)
        "sprop-stereo": useStereo,
        maxaveragebitrate: audioBitrate * 1024,
        // 'maxplaybackrate': , // 256 Kbits
        cbr: 1, // enable cbr
        useinbandfec: 1, // use inband fec
        usedtx: useDTX, // use dtx
        // Possible values are 3, 5, 10, 20, 40, 60, 120
        // https://tools.ietf.org/html/draft-ietf-payload-rtp-opus-11
        maxptime: 120, // chrome crashes if set anything less than 20, default is 120 - FRR
    });
    const arr = sdp.split("\r\n");
    arr.forEach((str, i) => {
        if (/^a=fmtp:\d*/.test(str)) {
            let br = `${str};x-google-max-bitrate=${bandwidth.video.max}`;
            br += `;x-google-min-bitrate=${bandwidth.video.min}`;
            br += `;x-google-start-bitrate=${bandwidth.video.target}`;
            arr[i] = br;
        }
        else if (/^a=mid:(1|video)/.test(str)) {
            arr[i] += "\r\nb=AS:10000";
        }
    });
    return arr.join("\r\n");
}
exports.defaultBandwithOptions = defaultBandwithOptions;
const BandwidthHandler = (function () {
    function setBAS(sdp, bandwidth, isScreen, isFirefox) {
        if (!bandwidth) {
            return sdp;
        }
        if (typeof isFirefox !== "undefined" && isFirefox) {
            return sdp;
        }
        if (isScreen) {
            if (!bandwidth.screen) {
                console.warn("It seems that you are not using bandwidth for screen. Screen sharing is expected to fail.");
            }
            else if (bandwidth.screen < 300) {
                console.warn("It seems that you are using wrong bandwidth value for screen. Screen sharing is expected to fail.");
            }
        }
        // if screen; must use at least 300kbs
        if (bandwidth.screen && isScreen) {
            sdp = sdp.replace(/b=AS([^\r\n]+\r\n)/g, "");
            sdp = sdp.replace(/a=mid:video\r\n/g, `a=mid:video\r\nb=AS:${bandwidth.screen}\r\n`);
        }
        // remove existing bandwidth lines
        if (bandwidth.audio || bandwidth.video || bandwidth.data) {
            sdp = sdp.replace(/b=AS([^\r\n]+\r\n)/g, "");
        }
        if (bandwidth.audio) {
            sdp = sdp.replace(/a=mid:audio\r\n/g, `a=mid:audio\r\nb=AS:${bandwidth.audio}\r\n`);
        }
        if (bandwidth.video) {
            sdp = sdp.replace(/a=mid:video\r\n/g, `a=mid:video\r\nb=AS:${isScreen ? bandwidth.screen : bandwidth.video}\r\n`);
        }
        return sdp;
    }
    // Find the line in sdpLines that starts with |prefix|, and, if specified,
    // contains |substr| (case-insensitive search).
    function findLine(sdpLines, prefix, substr) {
        return findLineInRange(sdpLines, 0, -1, prefix, substr !== null && substr !== void 0 ? substr : "");
    }
    // Find the line in sdpLines[startLine...endLine - 1] that starts with |prefix|
    // and, if specified, contains |substr| (case-insensitive search).
    function findLineInRange(sdpLines, startLine, endLine, prefix, substr) {
        const realEndLine = endLine !== -1 ? endLine : sdpLines.length;
        for (let i = startLine; i < realEndLine; ++i) {
            if (sdpLines[i].indexOf(prefix) === 0) {
                if (!substr ||
                    sdpLines[i].toLowerCase().indexOf(substr.toLowerCase()) !== -1) {
                    return i;
                }
            }
        }
        return null;
    }
    function findLines(sdpLines, prefix, substr) {
        return findLinesInRange(sdpLines, 0, -1, prefix, substr);
    }
    function findLinesInRange(sdpLines, startLine, endLine, prefix, substr) {
        const realEndLine = endLine !== -1 ? endLine : sdpLines.length;
        const foundLines = [];
        for (let i = startLine; i < realEndLine; ++i) {
            if (sdpLines[i].indexOf(prefix) === 0) {
                if (!substr ||
                    sdpLines[i].toLowerCase().indexOf(substr.toLowerCase()) !== -1) {
                    foundLines.push(i);
                }
            }
        }
        return foundLines.length > 0 ? foundLines : [];
    }
    // Gets the codec payload type from an a=rtpmap:X line.
    function getCodecPayloadType(sdpLine) {
        const pattern = new RegExp("a=rtpmap:(\\d+) \\w+\\/\\d+");
        const result = sdpLine.match(pattern);
        return result && result.length === 2 ? result[1] : null;
    }
    function setVideoBitrates(sdp, params) {
        params = params || {};
        const xgoogle_min_bitrate = params.min;
        const xgoogle_max_bitrate = params.max;
        const sdpLines = sdp.split("\r\n");
        // VP8
        const vp8Index = findLine(sdpLines, "a=rtpmap", "VP8/90000");
        let vp8Payload;
        if (vp8Index) {
            vp8Payload = getCodecPayloadType(sdpLines[vp8Index]);
        }
        if (!vp8Payload) {
            return sdp;
        }
        let appendrtxNext = "\r\n";
        appendrtxNext += `a=fmtp:${vp8Payload} x-google-min-bitrate=${xgoogle_min_bitrate || "228"}; x-google-max-bitrate=${xgoogle_max_bitrate || "228"}`;
        let appendedSdpLines = setVideoBitratesRtx(sdpLines, appendrtxNext);
        sdp = appendedSdpLines.join("\r\n");
        return sdp;
    }
    function setVideoBitratesRtx(sdpLines, appendrtxNext) {
        const rtxIndex = findLine(sdpLines, "a=rtpmap", "rtx/90000");
        let rtxPayload;
        if (rtxIndex) {
            rtxPayload = getCodecPayloadType(sdpLines[rtxIndex]);
        }
        if (!rtxIndex || !rtxPayload) {
            return sdpLines;
        }
        const rtxFmtpLineIndex = findLine(sdpLines, `a=fmtp:${rtxPayload.toString()}`);
        if (rtxFmtpLineIndex !== null) {
            sdpLines[rtxFmtpLineIndex] =
                sdpLines[rtxFmtpLineIndex].concat(appendrtxNext);
        }
        return sdpLines;
    }
    function setOpusAttributes(sdp, params) {
        params = params || {};
        const sdpLines = sdp.split("\r\n");
        // Opus
        const opusIndex = findLine(sdpLines, "a=rtpmap", "opus/48000");
        let opusPayload;
        if (opusIndex) {
            opusPayload = getCodecPayloadType(sdpLines[opusIndex]);
        }
        if (!opusPayload) {
            return sdp;
        }
        const opusFmtpLineIndexes = findLines(sdpLines, `a=fmtp:${opusPayload.toString()}`);
        if (opusFmtpLineIndexes.length === 0) {
            // not found
            return sdp;
        }
        // console.log("Length of opus indexes", opusFmtpLineIndexes);
        let appendOpusNext = "";
        appendOpusNext += `; stereo=${typeof params.stereo !== "undefined" ? params.stereo : "1"}`;
        appendOpusNext += `; sprop-stereo=${typeof params["sprop-stereo"] !== "undefined"
            ? params["sprop-stereo"]
            : "1"}`;
        if (typeof params.maxaveragebitrate !== "undefined") {
            appendOpusNext += `; maxaveragebitrate=${params.maxaveragebitrate || 128 * 1024 * 8}`;
        }
        if (typeof params.maxplaybackrate !== "undefined") {
            appendOpusNext += `; maxplaybackrate=${params.maxplaybackrate || 128 * 1024 * 8}`;
        }
        if (typeof params.cbr !== "undefined") {
            appendOpusNext += `; cbr=${typeof params.cbr !== "undefined" ? params.cbr : "1"}`;
        }
        if (typeof params.useinbandfec !== "undefined") {
            appendOpusNext += `; useinbandfec=${params.useinbandfec}`;
        }
        if (typeof params.usedtx !== "undefined") {
            appendOpusNext += `; usedtx=${params.usedtx}`;
        }
        if (typeof params.maxptime !== "undefined") {
            appendOpusNext += `\r\na=maxptime:${params.maxptime}`;
        }
        opusFmtpLineIndexes.forEach((opusFmtpLineIndex) => {
            sdpLines[opusFmtpLineIndex] =
                sdpLines[opusFmtpLineIndex].concat(appendOpusNext);
        });
        sdp = sdpLines.join("\r\n");
        return sdp;
    }
    return {
        setApplicationSpecificBandwidth(sdp, bandwidth, isScreen) {
            return setBAS(sdp, bandwidth, isScreen);
        },
        setVideoBitrates(sdp, params) {
            return setVideoBitrates(sdp, params);
        },
        setOpusAttributes(sdp, params) {
            return setOpusAttributes(sdp, params);
        },
    };
})();
// Own code, this function modifies an sdp given a codec and changes the payloadTypeID
// to a standard one, this is so that crossbrowser works thru the SFU
function modifyCodecPayloadType(sdp, codec, payloadType) {
    var _a, _b, _c, _d;
    const sdpLines = sdp.split("\r\n");
    // a=rtpmap:98 H264/90000
    // Search for line with codecType == payloadType.
    const aLineIndex = findLine(sdpLines, "a=rtpmap", payloadType);
    if (aLineIndex) {
        const codecIndex = findLine(sdpLines, "a=rtpmap", codec);
        if (codecIndex == aLineIndex)
            return sdp; // nothing to do everything is gucci
        // delete codec
        let index;
        sdpLines[aLineIndex] = "";
        while (findLine(sdpLines, "a=rtcp-fb", payloadType)) {
            index = (_a = findLine(sdpLines, "a=rtcp-fb", payloadType)) !== null && _a !== void 0 ? _a : -1;
            sdpLines[index] = "";
        }
        while (findLine(sdpLines, "a=fmtp", payloadType)) {
            index = (_b = findLine(sdpLines, "a=fmtp", payloadType)) !== null && _b !== void 0 ? _b : -1;
            sdpLines[index] = "";
        }
    }
    else {
        // add payloadType to mline
        const mLineIndex = findLine(sdpLines, "m=", "video");
        if (mLineIndex) {
            console.log("ADDING PAYLOAD TYPE");
            sdpLines[mLineIndex] = setDefaultCodec(sdpLines[mLineIndex], payloadType);
            console.log("ADDING PAYLOAD TYPE", sdpLines[mLineIndex]);
        }
    }
    const codecIndex = findLine(sdpLines, "a=rtpmap", codec);
    if (codecIndex) {
        const payload = getCodecPayloadType(sdpLines[codecIndex]);
        if (payload) {
            sdpLines[codecIndex] = changePayloadType(sdpLines[codecIndex], "a=rtpmap:", payloadType);
            let index;
            // change other lines too
            while (findLine(sdpLines, "a=rtcp-fb:", payload)) {
                index = (_c = findLine(sdpLines, "a=rtcp-fb:", payload)) !== null && _c !== void 0 ? _c : -1;
                // check if index is valid
                if (index == -1)
                    break;
                sdpLines[index] = changePayloadType(sdpLines[index], "a=rtcp-fb:", payloadType);
            }
            while (findLine(sdpLines, `a=fmtp:${payload}`, payload)) {
                index = (_d = findLine(sdpLines, "a=rtcp-fb:", payload)) !== null && _d !== void 0 ? _d : -1;
                if (index == -1)
                    break;
                sdpLines[index] = changePayloadType(sdpLines[index], "a=fmtp:", payloadType);
            }
        }
        // remove old Codec from m line
        // Search for m line.
        const mLineIndex = findLine(sdpLines, "m=", "video");
        if (mLineIndex === null) {
            return sdp;
        }
        sdpLines[mLineIndex] = removePayloadTypeFromMLine(sdpLines[mLineIndex], payload !== null && payload !== void 0 ? payload : "");
    }
    // remove empty lines
    const sdpLinesFiltered = sdpLines.filter((line) => line.length > 0);
    sdp = sdpLinesFiltered.join("\r\n");
    return sdp;
}
exports.modifyCodecPayloadType = modifyCodecPayloadType;
function removePayloadTypeFromMLine(mLine, payload) {
    const elements = mLine.split(" ");
    // Just copy the first three parameters; codec order starts on fourth.
    const newLine = elements.slice(0, 3);
    // Put target payload first and copy in the rest.
    for (let i = 3; i < elements.length; i++) {
        if (elements[i] !== payload) {
            newLine.push(elements[i]);
        }
    }
    return newLine.join(" ");
}
function changePayloadType(aLine, token, payload) {
    // a=rtpmap:98 H264/90000
    const elements = aLine.split(" ");
    elements[0] = token + payload;
    return elements.join(" ");
}
